import { ChangeEvent, ReactNode } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { JobType } from "types";

export interface JobTypeChecklistProps {
  readonly?: boolean;
  disabled?: JobType[];
  label: string;
  helperText?: string;
  jobTypes: JobType[];
  setJobTypes?: (jobTypes: JobType[]) => void;
  enabledJobTypes?: JobType[];
}

export const JobTypeChecklist = ({
  readonly = false,
  disabled = [],
  label,
  helperText,
  jobTypes = [],
  setJobTypes,
  enabledJobTypes = [],
}: JobTypeChecklistProps): ReactNode => {
  const { t } = useTranslation();

  const handleJobTypeChange = ({
    target: { name, checked },
  }: ChangeEvent<HTMLInputElement>) => {
    if (!setJobTypes) return;

    let newJobTypes = [...jobTypes];
    if (checked) {
      newJobTypes.push(name as JobType);
    } else {
      newJobTypes = jobTypes.filter((jobType) => jobType !== name);
    }

    setJobTypes(newJobTypes);
  };

  return (
    <Stack direction={"column"} spacing={0} height={{xs:"auto", md: 60}} sx={{ border: {xs: "1px solid lightgray", md: "none"}, p: {xs: 1, md: 0}, my: {xs: 1, md: 0} }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={0}>
        <Box
          sx={{
            height: 50,
            width: 305,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          <Typography
            variant={"body1"}
            align={"left"}
            textTransform={"capitalize"}
          >
            {label}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "row", md: "row" }}
          display={"flex"}
          spacing={1}
        >
          {enabledJobTypes.includes(JobType.TOW) && (
          <FormControlLabel
            sx={{ width: 76, height: 50, textTransform: "capitalize" }}
            aria-label={t("jobType.tow", { ns: "enums" })}
            id={`${label}-${JobType.TOW}`}
            control={
              <Checkbox
                disabled={disabled.includes(JobType.TOW)}
                name={JobType.TOW}
                readOnly={readonly}
                checked={jobTypes.includes(JobType.TOW)}
                onChange={handleJobTypeChange}
                aria-labelledby={`${label}-${JobType.TOW}`}
              />
            }
            label={t("jobType.tow", { ns: "enums" })}
          />
          )}
          {enabledJobTypes.includes(JobType.TAG) && (
          <FormControlLabel
            sx={{ width: 76, height: 50, textTransform: "capitalize" }}
            aria-label={t("jobType.tag", { ns: "enums" })}
            id={`${label}-${JobType.TAG}`}
            control={
              <Checkbox
                name={JobType.TAG}
                disabled={disabled.includes(JobType.TAG)}
                readOnly={readonly}
                checked={jobTypes.includes(JobType.TAG)}
                onChange={handleJobTypeChange}
                aria-labelledby={`${label}-${JobType.TAG}`}
              />
            }
            label={t("jobType.tag", { ns: "enums" })}
          />
          )}
          {enabledJobTypes.includes(JobType.BOOT) && (
          <FormControlLabel
            sx={{ width: 76, height: 50, textTransform: "capitalize" }}
            aria-label={t("jobType.boot", { ns: "enums" })}
            id={`${label}-${JobType.BOOT}`}
            control={
              <Checkbox
                name={JobType.BOOT}
                disabled={disabled.includes(JobType.BOOT)}
                readOnly={readonly}
                checked={jobTypes.includes(JobType.BOOT)}
                onChange={handleJobTypeChange}
                aria-labelledby={`${label}-${JobType.BOOT}`}
              />
            }
            label={t("jobType.boot", { ns: "enums" })}
          />
          )}
          {enabledJobTypes.includes(JobType.PATROL) && (
          <FormControlLabel
            sx={{ width: 76, height: 50, textTransform: "capitalize" }}
            aria-label={t("jobType.patrol", { ns: "enums" })}
            id={`${label}-${JobType.PATROL}`}
            control={
              <Checkbox
                name={JobType.PATROL}
                disabled={disabled.includes(JobType.PATROL)}
                readOnly={readonly}
                checked={jobTypes.includes(JobType.PATROL)}
                onChange={handleJobTypeChange}
                aria-labelledby={`${label}-${JobType.PATROL}`}
              />
            }
              label={t("jobType.patrol", { ns: "enums" })}
            />
          )}
        </Stack>
      </Stack>
      {helperText && (
        <Typography
          variant={"caption"}
          align={"left"}
          lineHeight={1}
          sx={{
            mt: -1,
            padding: 0,
            opacity: disabled.length === 4 ? 0.5 : 0.8,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Stack>
  );
};
